import React, { useState, useMemo } from 'react';
import { Button } from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import { SmsTemplate } from '~/types/accountInfo';
import WhatsappPreview from '../WhatsappPreview';
import { Dialog, DialogTitle, DialogContent, IconButton } from './styles';

// From { {{1}}: "Test" } To { "1": "Test" }
function clearCurlyBrace(obj: Record<string, string>) {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => {
      // remove all { and } characters
      const cleanKey = key.replace(/[{}]/g, '');
      return [cleanKey, value];
    })
  );
}

type TemplateParamsDialogProps = {
  template: SmsTemplate;
  onClose: () => void;
  onParamsFilled: (
    paramValues: Record<string, string>,
    processedMessage: string
  ) => void;
};

/**
 * Dialog component that allows users to input values for parameters in a WhatsApp template message.
 * It extracts parameters from the template's message text, renders input fields for each parameter,
 * and updates the message preview in real-time as the user fills in the values. The dialog can only be closed when all parameters have been filled out.
 */
export default function TemplateParamsDialog({
  template,
  onClose,
  onParamsFilled,
}: TemplateParamsDialogProps) {
  const {
    messageText,
    // @ts-ignore
    config: { components },
  } = template;

  // Extract replaceable parameters like ##firstname##
  const parameters = useMemo(() => {
    const paramRegex = /(\{\{.+?\}\})/g;
    const matches = messageText.matchAll(paramRegex);
    return Array.from(matches, match => match[1]);
  }, [messageText]);

  // State to store parameter values
  const [paramValues, setParamValues] = useState<Record<string, string>>(
    parameters.reduce((acc, param) => ({ ...acc, [param]: '' }), {})
  );

  // Check if all parameters have been filled
  const isEditionComplete = parameters.every(
    param => paramValues[param]?.trim() !== ''
  );

  // Replace parameters in messageText with actual values
  const processedMessageText = useMemo(() => {
    let text = messageText;
    Object.entries(paramValues).forEach(([param, value]) => {
      if (value) {
        text = text.split(param).join(value);
      }
    });
    return text;
  }, [messageText, paramValues]);

  // @ts-ignore
  const buttons = components.find(
    (component: { type: string }) => component.type === 'BUTTONS'
  )?.buttons;
  // @ts-ignore
  const media = components.find(
    (component: { type: string }) => component.type === 'HEADER'
  )?.media;

  const templateInfo = {
    bodyText: processedMessageText,
    ...(media && {
      mediaUrl: media.url,
      mediaType: media.type,
      templateType: 'media',
    }),
    ...(buttons?.length && {
      buttons,
    }),
  };

  const handleParamChange = (param: string, value: string) => {
    setParamValues(prev => ({ ...prev, [param]: value }));
  };

  function handleClose() {
    if (onClose) onClose();
    if (isEditionComplete && onParamsFilled) {
      onParamsFilled(clearCurlyBrace(paramValues), processedMessageText);
    }
  }

  return (
    <Dialog
      open
      aria-labelledby="template-params-editor-dialog"
      data-test-id="template-params-dialog"
      PaperProps={{ 'data-test-id': 'template-params-dialog' } as any}>
      <DialogTitle disableTypography>
        <span className="title">Edit Parameters</span>
        <IconButton
          data-test-id="template-params-close"
          color="primary"
          disableRipple
          aria-label="close"
          onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <div className="whatsapp-preview-container">
          {/* @ts-ignore */}
          <WhatsappPreview {...templateInfo} />
        </div>

        {/* FIXME: Style using styled components */}
        {parameters.length > 0 && (
          <div
            className="parameters-container"
            data-test-id="template-params-list"
            style={{
              marginBottom: '20px',
              marginTop: '20px',
              maxHeight: '210px',
              overflowY: 'auto',
            }}>
            {parameters.map(param => (
              <div key={param} style={{ marginBottom: '12px' }}>
                <label
                  htmlFor={param}
                  data-test-id={`template-param-label-${param}`}
                  style={{
                    display: 'block',
                    marginBottom: '4px',
                    fontWeight: 500,
                  }}>
                  {param}
                </label>
                <input
                  id={param}
                  data-test-id={`template-param-input-${param}`}
                  type="text"
                  value={paramValues[param]}
                  onChange={e => handleParamChange(param, e.target.value)}
                  placeholder={`Enter ${param}`}
                  maxLength={25}
                  pattern="[a-zA-Z0-9.,!@£$%&*_\-+;?]+"
                  style={{
                    width: 310,
                    padding: '8px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px',
                  }}
                />
              </div>
            ))}
          </div>
        )}

        <Button
          data-test-id="template-params-done"
          disableRipple
          color="primary"
          variant="contained"
          disabled={!isEditionComplete}
          style={{ textTransform: 'none', marginTop: 20 }}
          onClick={handleClose}>
          Done
        </Button>
      </DialogContent>
    </Dialog>
  );
}
